import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Boxes, Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(email.trim(), password);
      else await register(email.trim(), password, name.trim());
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8F9FA]" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#0F172A] text-white p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "32px 32px" }}
        />
        <div className="relative flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-[#2563EB] flex items-center justify-center">
            <Boxes size={22} />
          </div>
          <span className="font-heading font-bold tracking-tight text-lg">Canales · Logística</span>
        </div>
        <div className="relative">
          <p className="uppercase text-xs tracking-[0.2em] text-[#2563EB] font-semibold mb-4">Reporte STORIS · Logistical Scheduling</p>
          <h1 className="font-heading font-extrabold tracking-tight text-4xl xl:text-5xl leading-[1.05]">
            Conciliación de<br />Deliveries
          </h1>
          <p className="mt-6 text-slate-300 text-base max-w-md leading-relaxed">
            Importa el reporte de STORIS, normaliza sus datos y detecta pendientes,
            diferencias y excepciones operativas en segundos.
          </p>
        </div>
        <div className="relative text-xs text-slate-400 font-mono">v1.0 · Operación interna</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-9 w-9 rounded-sm bg-[#0F172A] flex items-center justify-center text-white">
              <Boxes size={18} />
            </div>
            <span className="font-heading font-bold tracking-tight">Conciliación de Deliveries</span>
          </div>

          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">
            {mode === "login" ? "Iniciar sesión" : "Crear cuenta"}
          </h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">
            Accede al panel de conciliación operativa.
          </p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs uppercase tracking-wide text-slate-500">Nombre</Label>
                <Input id="name" data-testid="register-name-input" value={name}
                  onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" className="rounded-sm" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-wide text-slate-500">Email</Label>
              <Input id="email" type="email" required data-testid="login-email-input" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="tucorreo@canales.com" className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-wide text-slate-500">Contraseña</Label>
              <Input id="password" type="password" required data-testid="login-password-input" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="rounded-sm" />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#F87171] rounded-sm px-3 py-2">
                {error}
              </div>
            )}

            <Button type="submit" disabled={busy} data-testid="login-submit-button"
              className="w-full rounded-sm bg-[#0F172A] hover:bg-[#0F172A]/90 h-10">
              {busy ? <Loader2 className="animate-spin" size={16} /> : (
                <>{mode === "login" ? "Entrar" : "Registrarme"} <ArrowRight size={16} /></>
              )}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-slate-200 flex-1" />
            <span className="text-xs text-slate-400 uppercase tracking-wide">o</span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>

          <Button variant="outline" onClick={googleLogin} data-testid="google-login-button"
            className="w-full rounded-sm h-10 border-slate-300">
            <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.6 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.5l6.2 5.3C40.9 36.7 44 31 44 24c0-1.3-.1-2.3-.4-3.5z" /></svg>
            Continuar con Google
          </Button>

          <p className="text-sm text-slate-500 mt-6 text-center">
            {mode === "login" ? "¿No tienes cuenta?" : "¿Ya tienes cuenta?"}{" "}
            <button type="button" data-testid="toggle-auth-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-[#2563EB] font-medium hover:underline">
              {mode === "login" ? "Regístrate" : "Inicia sesión"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
